import { observer } from 'mobx-react-lite';
import React from 'react';

import { Box } from '@mui/material';
import { Add } from '@mui/icons-material';
import { setAvailabilityOverride } from '@uplift/api';
import { DateFormats, providerScheduleViewTypeRoute } from '@uplift/constants';
import { useCalendarStore, useCurrentuser, useTimezone } from '@uplift/contexts';
import { useICSSubscriptionQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { CreateAppointmentMode, TimeRange } from '@uplift/types';
import { AddSessionFormType, AppointmentDurationMappingMinutesDuration, Button, CalendarPicker, CalendarRef } from '@uplift/ui-v2';

import { useCalendarSynchronizationDate } from './Sidebar.hooks';
import { SidebarAvailability } from './SidebarAvailability';
import { SidebarDetails } from './SidebarDetails';
import { SidebarICSIntegration } from './SidebarICSIntegration';
import { useIsCurrentProviderLive } from '@uplift/hooks';
import { useFormikContext } from 'formik';

export type ScheduleSidebarProps = {
  calendarRef: React.MutableRefObject<CalendarRef>;
};


export const ScheduleSidebar: React.FC<ScheduleSidebarProps> = observer((props) => {
  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);
  const formikContext = useFormikContext<AddSessionFormType>();
  const timezone = useTimezone();
  const calendarStore = useCalendarStore();
  const [ date, setDate ] = useCalendarSynchronizationDate(props.calendarRef);
  const shouldCreateAppointment = providerScheduleViewTypeRoute.useQueryParam('createAppointment');

  const handleAddSession = calendarStore.isCalendarLoading ? null : () => {
    calendarStore.setCreateEventMode(CreateAppointmentMode.new);
    calendarStore.getFirstAvailableCreateAppointmentRange({
      start: date,
      timezone: timezone,
      medicalRole: me.medicalRole,
    }).then(data => {
      const start = CalendarDateService.date(data.start).tz(timezone);
      const end = CalendarDateService.date(data.end).tz(timezone);
      const duration = end.diff(start, 'minutes') as keyof typeof AppointmentDurationMappingMinutesDuration;
      const newValues = {
        ...formikContext.values,
        date: start.format(DateFormats.ISO_DATE),
        startTime: start.format(),
        duration: AppointmentDurationMappingMinutesDuration[duration],
      };

      formikContext.setValues(newValues);
    });
  };

  const handleAddSessionRef = React.useRef(handleAddSession);

  handleAddSessionRef.current = handleAddSession;

  const hasAvailableSlots = React.useMemo(() => {
    return Array.from(calendarStore.allowedSlots)?.length > 0;
  }, [ calendarStore.allowedSlots ]);

  React.useEffect(() => {
    if (shouldCreateAppointment && hasAvailableSlots) {
      handleAddSessionRef.current?.();
    }
  }, [ shouldCreateAppointment, hasAvailableSlots ]);

  const handleAvailabilityDateOverride = React.useCallback(async (date: DateObject, ranges: TimeRange[]) => {
    await setAvailabilityOverride({
      date: date.format(DateFormats.ISO_DATE),
      ranges: ranges,
    });

    setDate(date);
    calendarStore.loadAllEvents(calendarStore._lastInfo, true);
  }, [ calendarStore, setDate ]);

  const isPast = React.useMemo(() => {
    return date.isBefore(CalendarDateService.date().tz(timezone), 'date');
  }, [ date, timezone ]);

  const { data: icsSubscription } = useICSSubscriptionQuery(me.id, { enabled: true });

  return (
    <Box>
      <Box>
        <Button
          disabled={isPast || !isProviderInLiveStatus || !hasAvailableSlots}
          onClick={handleAddSession}
          fullWidth
          variant="contained"
          color="secondary"
          startIcon={<Add />}
        >
          Add session
        </Button>
      </Box>
      <Box pt={2}>
        <CalendarPicker date={date} onDateSelect={setDate} />
      </Box>
      <Box pb={4}>
        <SidebarAvailability handleAvailabilityDateOverride={handleAvailabilityDateOverride} />
      </Box>
      {icsSubscription && (
        <Box pb={4}>
          <SidebarICSIntegration
            subscription={icsSubscription}
          />
        </Box>
      )}
      <Box>
        <SidebarDetails />
      </Box>
    </Box>
  );
});
